import { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FileTextIconOutlined from 'components/icons/FileTextIconOutlined';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Notification from 'components/ui/base/notification/Notification';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import useDeclarations from 'hooks/useDeclarations';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { arrivalErrorMessage } from 'hooks/useIcs2ArrivalNotification';
import axiosClient from 'config/axios';
import config from 'config/config';
import { Declaration } from 'store/declarations/declaration';
import { AISMessageTypes, ICS2MessageTypes, MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { createIrelandH7ImportDeclarationsWithBulkUpload, getTemplate } from 'store/declarations/client';
import { TemplateResponse } from 'store/template/template';
import { RcFile } from 'antd/lib/upload';
import { uploadFile } from 'utils/uploadFile';
import { documentsPreUpload, confirmUploadFile } from 'store/documents/client';
import { attachDocumentToDeclaration } from 'store/file-upload/utils';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { createDeclarations } from 'views/invoice-upload/utils';
import { ManifestTabKeys } from 'views/manifests/manifest-details/types';
import { createIcs2EnsDeclarations, listManifestEnsDeclarations } from 'store/manifests/client';
import { createArrivalForManifest } from 'views/ics2-arrival-notifications/manifestArrival';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { useBrokerUploadContext } from './BrokerUploadProvider';
import EnsAlreadyExistsModal from '../../manifests/manifest-details/components/EnsAlreadyExistsModal';
import { BrokerUploadFormFields } from './types';
import CreateMultipleEnsModal from 'views/manifests/manifest-details/components/CreateMultipleEnsModal';

const BrokerUploadHeader = () => {
    const { isReadyToSubmit } = useMultiStepFormContext();
    const { values } = useFormikContext<BrokerUploadFormFields>();
    const { country, type, locationState, selectedJob, selectedManifest, manifestId } = useBrokerUploadContext();
    const { t } = useTranslation('customs_declarations');
    const navigate = useNavigate();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const {
        error,
        createIrelandImportDeclaration,
        createIrelandH7ImportDeclaration,
        createIrelandExportDeclaration,
        createUkExportDeclaration,
        createEntrySummaryDeclaration,
        createElectronicTransportDocument,
        createTemporaryStorageDeclaration,
        createArrivalAtExitDeclaration,
        createUkImportNewDeclaration,
        duplicateDeclaration,
        createGvmsRecord,
        createPbnRecord,
        createNctsDeclaration,
        createUkNctsDeclaration,
        createEnsIcs2Declaration,
        getDeclaration,
    } = useDeclarations();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState<'existingEns' | 'multipleEns' | null>(null);
    const [manifestHasExistingEns, setManifestHasExistingEns] = useState(false);
    // Tracks whether a fill-form attempt was made, regardless of success/failure.
    // Required because useDeclarations hooks store errors in Redux instead of throwing,
    // so the catch block alone cannot detect API failures.
    const [attemptedFillForm, setAttemptedFillForm] = useState(false);

    const hasBulkFile = !!values.bulkUpload.s3Link;
    const pageTitle = country && type ? t(`create${country}Declaration${type}`) : '';

    const getCustomerId = () => selectedJob?.customer?.id || selectedJob?.customerId;

    const isAis = country === DeclarationCountry.IRELAND && type === DeclarationInternalType.IMPORT;
    const isAes = country === DeclarationCountry.IRELAND && type === DeclarationInternalType.EXPORT;
    const isNcts = country === DeclarationCountry.IRELAND && type === DeclarationInternalType.NCTS;
    const isCds = country === DeclarationCountry.UK && type !== DeclarationInternalType.NCTS;
    const isICS2ArrivalNotification =
        country === DeclarationCountry.EU && type === DeclarationInternalType.ARRIVAL_NOTIFICATION_ICS2;
    const isICS2ENS = country === DeclarationCountry.EU && type === DeclarationInternalType.ENS;

    const handleCreateInvoice = async (declaration: Partial<Declaration>) => {
        const invoiceFile = values.invoice.invoiceFile as RcFile | null;
        if (!invoiceFile || !declaration.id || !declaration.customerId) return;

        const payload = {
            fileSize: invoiceFile.size,
            customerId: declaration.customerId,
            declarationId: declaration.id,
            fileName: invoiceFile.name,
        };

        try {
            const { preSignedUrl } = await documentsPreUpload(payload);
            const match = preSignedUrl.match(/\/([^/?]+)\?/);
            const uuid = match?.[1];

            if (!uuid) throw new Error('UUID is missing');

            await uploadFile(invoiceFile, preSignedUrl);
            const { payload: confirmPayload } = await confirmUploadFile({
                s3id: uuid,
                ...payload,
            });

            await attachDocumentToDeclaration(
                {
                    fileId: confirmPayload.id,
                    fileReference: confirmPayload.link,
                    fileName: confirmPayload.filename,
                    fileInfo: { documentType: confirmPayload.contentType },
                    invoice: true,
                },
                confirmPayload.declarationId,
                { isAis, isAes, isNcts, isCds }
            );
            await getDeclaration(declaration.id);
        } catch {
            message.error(
                'Invoice uploading failed! Please create a new declaration in order to attach an invoice to it.',
                5
            );
        }
    };

    const handleFillForm = async () => {
        if (isICS2ArrivalNotification) {
            await handleCreateIcs2ArrivalNotification();
            return;
        }

        const jobId = values.job.jobId;
        const customerId = getCustomerId();

        if (!jobId || !customerId || !country || !type) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing required data to create declaration',
                placement: 'top',
            });
            return;
        }

        setIsSubmitting(true);
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Creating declaration...' } });

        try {
            let result: Partial<Declaration> | undefined;

            if (locationState?.declaration?.id) {
                result = (await duplicateDeclaration(locationState.declaration.id, customerId, jobId)) as Declaration;
            } else {
                const messageType = values.formType.formType as MessageTypes | undefined;
                const declarationRef = values.declaration.declarationId
                    ? { id: values.declaration.declarationId, mrn: values.declaration.mrn }
                    : undefined;

                let template: TemplateResponse | undefined;
                if (values.template.templateId) {
                    template = await getTemplate(values.template.templateId);
                }

                result = await createDeclarations(
                    country,
                    customerId,
                    jobId,
                    type,
                    createIrelandH7ImportDeclaration,
                    createIrelandImportDeclaration,
                    createEntrySummaryDeclaration,
                    createTemporaryStorageDeclaration,
                    createIrelandExportDeclaration,
                    createElectronicTransportDocument,
                    createUkExportDeclaration,
                    createUkImportNewDeclaration,
                    createArrivalAtExitDeclaration,
                    createGvmsRecord,
                    createPbnRecord,
                    createNctsDeclaration,
                    createUkNctsDeclaration,
                    createEnsIcs2Declaration,
                    messageType || undefined,
                    template,
                    declarationRef
                );
            }

            if (result) {
                await handleCreateInvoice(result);
                const declarationId = (result as Declaration).id;
                const jobId = values.job.jobId;
                const destination =
                    manifestId && jobId
                        ? `/manifests/${manifestId}/${jobId}/${declarationId}`
                        : `/declarations/${declarationId}`;
                navigate(destination, { replace: true });
                return;
            }
        } catch {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Could not create declaration.',
                description: 'Something went wrong. Please refresh the page and try again.',
            });
        } finally {
            setIsSubmitting(false);
            hideGlobalOverlay();
            setAttemptedFillForm(true);
        }
    };

    /**
     * The ICS2 arrival notification is an aggregate of its own, so it is created directly rather
     * than through `createDeclarations`, and the draft opens straight away for the operator to fill
     * in the transport details and the time of arrival.
     *
     * `createArrivalForManifest` owns the link between the two - see `manifestArrival.ts`.
     */
    const handleCreateIcs2ArrivalNotification = async () => {
        if (!manifestId) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Select a manifest before creating an arrival notification',
                placement: 'top',
            });
            return;
        }

        setIsSubmitting(true);
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Creating arrival notification...' } });

        try {
            const arrival = await createArrivalForManifest(manifestId);

            navigate(`/arrival-notifications/${arrival.id}`, { replace: true });
        } catch (error) {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Could not create the arrival notification.',
                description: arrivalErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setIsSubmitting(false);
            hideGlobalOverlay();
        }
    };

    /**
     * ICS2 ENS is generated from the manifest's AIS H1 declarations rather than from a job, so it
     * takes its own endpoint: F11 returns one declaration for the whole manifest, F50 one per job.
     */
    const handleCreateIcs2Ens = async () => {
        const ics2Type = values.formType.formType as ICS2MessageTypes | undefined;
        const entryTransportType = values.formType.entryTransportType;
        const templateId = values.template.templateId;

        if (!manifestId || !ics2Type || entryTransportType == null) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing required data to create ENS declarations',
                placement: 'top',
            });
            return;
        }

        setIsSubmitting(true);
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Creating ENS declarations...' } });

        try {
            const created = await createIcs2EnsDeclarations(manifestId, {
                ics2Type,
                entryTransportType: String(entryTransportType),
                templateId,
            });

            if (!created.length) {
                throw new Error('No ENS declarations were created for this manifest.');
            }

            // Several declarations have no single form to open, so the manifest ENS tab lists them.
            navigate(
                created.length === 1
                    ? `/declarations/${created[0].declarationId}`
                    : `/manifests/${manifestId}/${ManifestTabKeys.ENS}`,
                { replace: true }
            );
        } catch (error) {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Could not create ENS declarations.',
                description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setIsSubmitting(false);
            hideGlobalOverlay();
        }
    };

    const handleBulkSubmit = async () => {
        const s3Link = values.bulkUpload.s3Link;
        const customerId = getCustomerId();
        const jobId = values.job.jobId;
        const templateId = values.template.templateId;
        const messageType = values.formType.formType;

        if (!s3Link || !customerId || !jobId) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing required data',
                placement: 'top',
            });
            return;
        }

        setIsSubmitting(true);
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Creating declarations...' } });

        try {
            if (messageType === AISMessageTypes.H7) {
                await createIrelandH7ImportDeclarationsWithBulkUpload(
                    s3Link,
                    customerId,
                    jobId,
                    templateId ?? undefined
                );
                navigate(`/declarations/ireland/IMPORT`, { replace: true });
            } else {
                let url = `${config.declarationsUrl}/declarations/bulk/upload?s3Link=${s3Link}&customerId=${customerId}&jobId=${jobId}`;
                if (templateId) url += `&templateId=${templateId}`;
                await axiosClient.post(url, {});
                navigate(`/customs-declarations/${country}/${type}`, { replace: true });
            }
        } catch {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Could not create declarations.',
                description: 'Something went wrong. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
            hideGlobalOverlay();
        }
    };

    useEffect(() => {
        if (error && attemptedFillForm) {
            setAttemptedFillForm(false);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Could not create declaration.',
                description: error.message ?? 'Something went wrong. Please refresh the page and try again.',
            });
        }
    }, [error, attemptedFillForm]);

    const isReady = isReadyToSubmit();

    // Checks wheather there is already existing ENS for the declaration
    useEffect(() => {
        if (!isICS2ENS || !manifestId) {
            setManifestHasExistingEns(false);
            return;
        }

        let cancelled = false;

        listManifestEnsDeclarations(manifestId, { size: 1, archived: false })
            .then(({ total }) => {
                if (!cancelled) setManifestHasExistingEns(total > 0);
            })
            .catch(() => {
                if (!cancelled) setManifestHasExistingEns(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isICS2ENS, manifestId]);

    // F50 generates one ENS per job; F11 covers the whole manifest with a single declaration.
    const createsMultipleEns =
        values.formType.formType === ICS2MessageTypes.F50 && (selectedManifest?.jobs?.length ?? 0) > 1;

    const submit = () => {
        if (isICS2ENS) return handleCreateIcs2Ens();
        return hasBulkFile ? handleBulkSubmit() : handleFillForm();
    };

    const needsMultipleEnsConfirm = createsMultipleEns && !locationState?.multipleEnsConfirmed;

    const handleSubmitClick = () => {
        if (isICS2ENS && manifestHasExistingEns) {
            setConfirmation('existingEns');
            return;
        }

        if (isICS2ENS && needsMultipleEnsConfirm) {
            setConfirmation('multipleEns');
            return;
        }

        submit();
    };

    const handleExistingEnsConfirm = () => {
        if (needsMultipleEnsConfirm) {
            setConfirmation('multipleEns');
            return;
        }

        setConfirmation(null);
        submit();
    };

    const handleViewExistingEns = () => {
        setConfirmation(null);
        if (manifestId) navigate(`/manifests/${manifestId}/${ManifestTabKeys.ENS}`);
    };

    const handleMultipleEnsConfirm = () => {
        setConfirmation(null);
        submit();
    };

    return (
        <>
            <PageHeader
                title={pageTitle}
                //TODO: Add country flag icons in the follow up work item #7224
                icon={<FileTextIconOutlined />}
                sidebar={
                    <div>
                        <Button style={{ marginRight: 16 }} onClick={() => navigate(-1)}>
                            Cancel
                        </Button>
                        {hasBulkFile ? (
                            <Button
                                disabled={!isReady}
                                loading={isSubmitting}
                                type="primary"
                                onClick={handleSubmitClick}
                            >
                                {t('CreateBulkDeclarations')}
                            </Button>
                        ) : (
                            <Button
                                disabled={!isReady}
                                loading={isSubmitting}
                                type="primary"
                                onClick={handleSubmitClick}
                            >
                                {t('fillForm')}
                            </Button>
                        )}
                    </div>
                }
            />

            <EnsAlreadyExistsModal
                open={confirmation === 'existingEns'}
                onConfirm={handleExistingEnsConfirm}
                onViewExisting={handleViewExistingEns}
                onClose={() => setConfirmation(null)}
            />

            <CreateMultipleEnsModal
                open={confirmation === 'multipleEns'}
                onConfirm={handleMultipleEnsConfirm}
                onClose={() => setConfirmation(null)}
            />
        </>
    );
};

export default BrokerUploadHeader;
